import { Injectable } from '@angular/core';
import { GenericService } from '../../../core/services/GenericService';
import { ApiService } from '../../../core/services';
import { IArticle, IArticleVariant } from '../../../core/interfaces';
import { Observable } from 'rxjs';
import { ISimpleListItem } from '../../../core/interfaces/ISimpleListItem';
import { HttpParams } from '@angular/common/http';
import * as moment from 'moment/moment';
import { environment } from '../../../../environments/environment';
import { IArticleSalesHistoryItem } from './interfaces/IArticleSalesHistoryItem';
import { IArticlePriceSheet } from './interfaces/IArticlePriceSheet';
import { ITableResults } from '../../../core/interfaces/ITableResults';
import { IDateRangeSalesHistoryItem } from './interfaces/IDateRangeSalesHistoryItem';
import { HttpUtils } from '../../../core/utils/HttpUtils';
import { IMainStoreInactiveVariant } from '../../welcome/core/interfaces/IMainStoreInactiveVariant';
import {
  IArticleSalesForecastOptions,
  ICreateSalesForecastRequest
} from './interfaces/IArticleSalesForecastOptions';
import { IArticleSalesForecastRun } from './interfaces/IArticleSalesForecastRun';

@Injectable({
  providedIn: 'root'
})
export class ArticlesService<T = IArticle> extends GenericService<T> {
  protected readonly baseUrl = '/articles';

  constructor(public apiService: ApiService) {
    super(apiService);
  }

  getSimpleList( search = '', onlyBagable= false, results = 50): Observable<ISimpleListItem[]> {
    const params = new HttpParams()
      .append('search', `${search}`)
      .append('onlyBagable', `${onlyBagable}`)
      .append('results', `${results}`);
    return this.apiService.get(`${this.baseUrl}/simple`, params);
  }

  getArticlesVariants( search = '', onlyBagable = false, onlyRecipeCards = false, results = 50): Observable<ISimpleListItem[]> {
    const params = new HttpParams()
      .append('search', `${search}`)
      .append('onlyBagable', `${onlyBagable}`)
      .append('onlyRecipeCards', `${onlyRecipeCards}`)
      .append('results', `${results}`);
    return this.apiService.get(`${this.baseUrl}/variants/simple`, params);
  }

  getArticleByEan(ean: string) {
    return this.apiService.get(`${this.baseUrl}/variants/ean/${ean}`);
  }

  public getVariantsByArticleId(articleId: number): Observable<IArticleVariant[]> {
    return this.apiService.get(`${this.baseUrl}/${ articleId }/variants`);
  }

  public getVariantById(variantId: number): Observable<IArticleVariant> {
    return this.apiService.get(`${this.baseUrl}/variants/${ variantId }`);
  }

  public updateArticleVariant(variant: IArticleVariant, variantId: number): Observable<any> {
    return this.apiService.put(`${this.baseUrl}/variants/${ variantId }`, variant);
  }

  public createArticleVariant(variant: IArticleVariant): Observable<any> {
    return this.apiService.post(`${this.baseUrl}/variants`, variant);
  }

  public setDefaultVariant(variantId: number): Observable<any> {
    return this.apiService.post(`${this.baseUrl}/variants/${ variantId }/set-default`);
  }

  public removeArticleVariant(variantId: number): Observable<any> {
    return this.apiService.delete(`${this.baseUrl}/variants/${ variantId }`);
  }

  public updateArticleMinStock(
    articleId: number | null,
    variantId: number | null,
    minStock: number,
    productionArticleId?: number
  ): Observable<void> {
    return this.apiService.post(`${this.baseUrl}/min-stock`, {
      articleId,
      articleVariantId: variantId,
      minStock,
      productionArticleId
    });
  }


  public updateMHD(variantId: number, mhd: string): Observable<void> {
    return this.apiService.patch(`${this.baseUrl}/variants/${variantId}/change-mhd`, { mhd });
  }

  public getArticlesCategories(search = '', results = 10): Observable<ISimpleListItem[]> {
    const params = new HttpParams().append('search', search).append('results', results.toString());

    return this.apiService.get(`${ this.baseUrl }/get-articles-categories`, params);
  }

  public getTopProductsSalesHistory(
    count: number,
    dateFrom: moment.Moment,
    dateTo: moment.Moment
  ): Observable<IArticleSalesHistoryItem[]> {
    return this.apiService.get(
      `${ this.baseUrl }/sales-history/top-products`,
      new HttpParams()
        .append('count', count)
        .append('dateFrom', dateFrom.format(environment.servicesDateFormat))
        .append('dateTo', dateTo.format(environment.servicesDateFormat))
    );
  }

  public getArticleSalesHistory(
    articleId: number,
    dateFrom?: moment.Moment,
    dateTo?: moment.Moment
  ): Observable<IArticleSalesHistoryItem[]> {
    let params = new HttpParams().append('aggregate', 'true');

    if (dateFrom) {
      params = params.append('dateFrom', dateFrom.format(environment.servicesDateFormat));
    }

    if (dateTo) {
      params = params.append('dateTo', dateTo.format(environment.servicesDateFormat));
    }

    return this.apiService.get(`${ this.baseUrl }/${ articleId }/sales-history`, params);
  }

  public getArticlePriceSheet(
    articleId: number,
    dateFrom: moment.Moment,
    dateTo: moment.Moment
  ): Observable<IArticlePriceSheet[]> {
    return this.apiService.get(
      `${ this.baseUrl }/${ articleId }/price-sheet`,
      new HttpParams()
        .append('dateFrom', dateFrom.format(environment.servicesDateFormat))
        .append('dateTo', dateTo.format(environment.servicesDateFormat))
    );
  }

  public getAllArticlesDateRangeSalesHistory(
    page: number,
    pageSize: number,
    dateFrom?: moment.Moment,
    dateTo?: moment.Moment,
  ): Observable<ITableResults<IDateRangeSalesHistoryItem>> {
    let params = new HttpParams()
      .append('pageIndex', page.toString())
      .append('pageSize', pageSize.toString());

    if (dateFrom) {
      params = params.append('dateFrom', dateFrom.format(environment.servicesDateFormat));
    }

    if (dateTo) {
      params = params.append('dateTo', dateTo.format(environment.servicesDateFormat));
    }

    return this.apiService.get(`${ this.baseUrl }/date-range-sales-history`, params);
  }

  public getMainStoreInactiveVariantsWithStock(
    pageIndex: number,
    pageSize: number,
    sortField: string,
    sortOrder: string,
    filters: Array<{ key: string; value: string[] }>,
    search: Array<{ key: string; value: string[] }>,
    additionalFilters: Array<{ key: string; value: string[] }>
  ): Observable<ITableResults<IMainStoreInactiveVariant>> {
    return this.apiService.get(
      `${this.baseUrl}/variants/main-store-inactive-with-stock`,
      HttpUtils.prepareQueryableParams(pageIndex, pageSize, sortField, sortOrder, filters, search, additionalFilters)
    );
  }

  public getSalesForecastOptions(): Observable<IArticleSalesForecastOptions> {
    return this.apiService.get(`${this.baseUrl}/sales-forecast/options`);
  }

  public startSalesForecast(
    articleId: number,
    request: ICreateSalesForecastRequest
  ): Observable<IArticleSalesForecastRun> {
    return this.apiService.post(`${this.baseUrl}/${articleId}/sales-forecast`, request);
  }

  public getSalesForecastById(articleId: number, forecastId: number): Observable<IArticleSalesForecastRun> {
    return this.apiService.get(`${this.baseUrl}/${articleId}/sales-forecast/${forecastId}`);
  }

  public getLatestSalesForecast(articleId: number): Observable<IArticleSalesForecastRun> {
    return this.apiService.get(`${this.baseUrl}/${articleId}/sales-forecast/latest`);
  }
}
